import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnFlexContainer, ContainerV2 } from '../../../stories/atoms/containerV2/ContainerV2';
import { useThemeContext } from '../../../global-state/themeContext';
import { IconWithText } from '../../../stories/molecules';
import { Banner, BannerItemsType } from '../../../stories/molecules/banner';
import { Icons, Typography } from '../../../stories/atoms';
import { permissionsTranslations, employeesTranslations, errors } from '../../../translations';
import { Profile } from '../../../global-state/types';
import { getUniqueId } from '../../../utils';
import { permissionConfigs } from './utils';
import { PermissionsTable } from './PermissionsTable';
import { StyledContainer } from './Permissions.styles';

type PermissionsProps = {
  isMobile: boolean;
  employee?: Profile;
};

type PermissionView = 'main' | 'page_permissions' | 'widget_permissions' | 'settings_permissions';

const {
  title,
  pagePermissions,
  pagePermissionsDescription,
  employeePagePermissionsDescription,
  widgetPermissions,
  widgetPermissionsDescription,
  employeeWidgetPermissionsDescription,
  settingsPermissions,
  settingsPermissionsDescription,
  employeeSettingsPermissionsDescription,
  viewPermissions,
} = permissionsTranslations;

export const Permissions: FC<PermissionsProps> = ({ isMobile, employee }) => {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const [currentView, setCurrentView] = useState<PermissionView>('main');
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);

  const {
    editPermissionModal: { successNotification },
  } = employeesTranslations;

  const { permissionError } = errors;

  const permissionItems = [
    {
      id: 'page_permissions' as const,
      icon: 'LIST' as const,
      titleKey: pagePermissions.key,
      titleDefault: pagePermissions.defaultValue,
      descriptionKey: employee ? employeePagePermissionsDescription.key : pagePermissionsDescription.key,
      descriptionDefault: employee
        ? employeePagePermissionsDescription.defaultValue
        : pagePermissionsDescription.defaultValue,
    },
    {
      id: 'widget_permissions' as const,
      icon: 'ALLWIDGETS' as const,
      titleKey: widgetPermissions.key,
      titleDefault: widgetPermissions.defaultValue,
      descriptionKey: employee ? employeeWidgetPermissionsDescription.key : widgetPermissionsDescription.key,
      descriptionDefault: employee
        ? employeeWidgetPermissionsDescription.defaultValue
        : widgetPermissionsDescription.defaultValue,
    },
    {
      id: 'settings_permissions' as const,
      icon: 'SETTINGS' as const,
      titleKey: settingsPermissions.key,
      titleDefault: settingsPermissions.defaultValue,
      descriptionKey: employee ? employeeSettingsPermissionsDescription.key : settingsPermissionsDescription.key,
      descriptionDefault: employee
        ? employeeSettingsPermissionsDescription.defaultValue
        : settingsPermissionsDescription.defaultValue,
    },
  ];

  const handleViewPermissions = (permissionId: PermissionView) => {
    setCurrentView(permissionId);
  };

  const handleBackToMain = () => {
    setCurrentView('main');
  };

  useEffect(() => {
    setCurrentView('main');
  }, [employee?.id]);

  const handleSubmit = (isSuccess: boolean, _type: string) => {
    const label = isSuccess
      ? t(successNotification.key, successNotification.defaultValue, {
          firstName: employee?.firstName,
          lastName: employee?.lastName,
        })
      : t(permissionError.key, permissionError.defaultValue);

    setBannerItems((items) => [
      ...items,
      {
        id: getUniqueId(),
        label,
        type: isSuccess ? ('success' as const) : ('error' as const),
      },
    ]);
  };

  const renderDetailView = () => {
    if (currentView === 'main') return null;

    const config = permissionConfigs[currentView];
    if (!config) return null;

    return (
      <PermissionsTable
        handleBackToMain={handleBackToMain}
        config={config}
        employee={employee}
        handleSubmit={handleSubmit}
      />
    );
  };

  const renderPermissionItem = (item: (typeof permissionItems)[0]) => (
    <ContainerV2 key={item.id} padding={isMobile ? [0, 0, 2] : [4]} data-testid={`permission-item-${item.id}`}>
      <IconWithText
        label={t(item.titleKey, item.titleDefault)}
        iconOptions={{ icon: Icons[item.icon], size: 'small' }}
        textOptions={{ type: 'h4' }}
      />
      <Typography type={isMobile ? 'caption' : 'body2'} gutterBottom={!isMobile}>
        {t(item.descriptionKey, item.descriptionDefault)}
      </Typography>

      <ContainerV2 display="flex" justifyContent="end" padding={[4, 0, 0]}>
        <IconWithText
          data-testid={`view-${item.id}-button`}
          label={t(viewPermissions.key, viewPermissions.defaultValue)}
          onClick={() => handleViewPermissions(item.id)}
          iconOptions={{ icon: Icons.ARROW }}
          textOptions={{ type: 'body3' }}
          labelPosition="left"
        />
      </ContainerV2>
    </ContainerV2>
  );

  return (
    <ColumnFlexContainer
      data-testid="permissions-section"
      backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
      hasBorder
      borderRadius={[2]}
    >
      <Banner items={bannerItems} onChange={(items) => setBannerItems(items)} />
      <ContainerV2
        backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
        padding={isMobile ? [3, 4] : [4, 8]}
        borderRadius={[4, 4, 0, 0]}
      >
        <IconWithText
          label={t(title.key, title.defaultValue)}
          iconOptions={{ icon: Icons.KEY, size: 'small' }}
          textOptions={{ type: 'h4' }}
        />
      </ContainerV2>

      {currentView === 'main' ? (
        <StyledContainer gap={[4]} isMobile={isMobile} padding={[4]}>
          {permissionItems.map(renderPermissionItem)}
        </StyledContainer>
      ) : (
        renderDetailView()
      )}
    </ColumnFlexContainer>
  );
};
